import React from 'react';
import PropTypes from 'prop-types';
import Dropzone from 'react-dropzone';
import cx from 'classnames';
import { isEqual, isEmpty } from 'lodash';

import { cropImageToDataURL, generateImageName, getOrientation } from './utils/imageHelpers';

import * as Icons from '../icons/Icons';
import PhotoCropper from './PhotoCropper';
import PromoteGalleryToHero from './PromoteGalleryToHero';
import UploadProgressIndicator from './UploadProgressIndicator';
import Loading from '../Loading';
import TextField from '../TextField';
import Heading from '../Heading';
import FocusOnMount from '../FocusOnMount';
import StyledModal, { ModalActions } from '../Modal';
import Button, { ButtonGroup } from '../Button';

import { UPDATE, UPLOAD, DELETE, CROP } from './utils/UploadManager';
import { IMAGE_SHAPE, CROPPED_IMAGE_SHAPE, UPLOAD_MANAGER_SHAPE } from './shapes';
import TextArea from '../TextArea/TextArea';
import { FlexCol, FlexRow } from '../FlexGrid';

/**
 * SinglePhotoManager
 * ----------------------------------------------------------------------------
 * Props:
 *
 * id ................. Used for both dom elements and selecting items from the gallery array
 * imageType .......... Tells the API what type the uploaded image is
 * imageDisplayType ... Used for button labels and modal titles
 * originalImage ...... An object containing the image source and specs
 * croppedImage ....... An object containing the image source and specs
 * altText ............ Populates the alternate text field
 * caption ............ Populates the caption field
 * credit ............. Populates the credit field
 * desiredWidth ....... The optimum width for the cropped image
 * desiredHeight ...... The optimum height for the cropped image
 * uploadManager ...... The instance of UploadManager
 * onPublish .......... Callback after a new photo has been successfully uploaded
 * onUpdate ........... Callback after the altText, caption or credit is successfully updated
 * onCancel ........... Callback after the user cancels editing the photo, altText, caption or credit
 * onDelete ........... Callback after the photo and text has been successfully deleted
 * hasHeroImage ....... [Preview Image] True if a hero image has been published
 * getHeroImage ....... [Preview Image] Function that returns the hero image data
 * numberOfGalleryItems [Gallery Image] The number of published gallery items; used for changing item position
 * position ........... [Gallery Image] The index of the item in the gallery array
 * reorderGallery ..... [Gallery Image] Function that changes the order of the item in the gallery array
 * isHeroImage......... Display a slightly different layout for the hero grid images
 * showFilesInput...... Show file picker on display for a new image
 * showCropOnSelect.... Show image crop tool on file picker select
 * showCropOnMount.... Show image crop tool on component mount (for new images)
 * isFreeformCrop...... Show image crop tool on file picker select
 *
 *
 * Shape of originalImage:
 *     {
 *         name: string, the sluggified filename
 *         source: string, dataURL (unpublished), url (published)
 *         width: number, width in pixels
 *         height: number, height in pixels
 *         size: number, size in bytes (only unpublished)
 *         mediaId: string, server-generated UUID (only published)
 *     }
 *
 * Shape of croppedImage:
 *     {
 *         percentCrop: {
 *             x: number, crop origin in percent of original image
 *             y: number: crop origin in percent of original image
 *             height: crop image height as a percent of original image's height
 *             width: crop image width as a percent of original image's width
 *         },
 *         pixelCrop: {
 *             x: number, crop origin in pixels
 *             y: number: crop origin in pixels
 *             height: crop image height in pixels
 *             width: crop image width in pixels
 *         }
 *         name: string, the sluggified filename
 *         source: string, dataURL (unpublished), url (published)
 *         width: number, width in pixels
 *         height: number, height in pixels
 *         size: number, size in bytes (only unpublished)
 *         mediaId: string, server-generated UUID (only published)
 *     }
 */
export default class SinglePhotoManager extends React.PureComponent {
    static propTypes = {
        id: PropTypes.string.isRequired,
        imageType: PropTypes.oneOf(['hero', 'preview', 'gallery']).isRequired,
        imageTypeDisplay: PropTypes.string.isRequired,
        originalImage: IMAGE_SHAPE,
        croppedImage: CROPPED_IMAGE_SHAPE,
        altText: PropTypes.string,
        caption: PropTypes.string,
        credit: PropTypes.string,
        desiredWidth: PropTypes.number,
        desiredHeight: PropTypes.number,
        uploadManager: UPLOAD_MANAGER_SHAPE.isRequired,
        onPublish: PropTypes.func,
        onUpdate: PropTypes.func,
        onCancel: PropTypes.func,
        onDelete: PropTypes.func,

        // For preview image
        hasHeroImage: PropTypes.bool,
        getHeroImage: PropTypes.func,

        // For gallery images
        numberOfGalleryItems: PropTypes.number,
        position: PropTypes.number,
        galleryPosition: PropTypes.number,
        maxGalleryPosition: PropTypes.number,
        reorderGallery: PropTypes.func,
        onHeroPromotion: PropTypes.func,
        getIsAltTextUnique: PropTypes.func,

        heroImageName: PropTypes.string,
        isHeroImage: PropTypes.bool,
        showFilesInput: PropTypes.bool,
        showCropOnSelect: PropTypes.bool,
        showCropOnMount: PropTypes.bool,
        isFreeformCrop: PropTypes.bool,
    };

    static defaultProps = {
        originalImage: {},
        croppedImage: {},
        altText: '',
        caption: '',
        credit: '',
        desiredWidth: 700,
        desiredHeight: 524,
        hasHeroImage: false,
        numberOfGalleryItems: 0,
        position: 0,
        galleryPosition: 0,
        maxGalleryPosition: 0,
        showFilesInput: false,
        showCropOnSelect: true,
        isFreeformCrop: false,
        getHeroImage: () => { },
        onPublish: () => { },
        onUpdate: () => { },
        onDelete: () => { },
        onCancel: () => { },
        reorderGallery: () => { },
        onHeroPromotion: () => { },
        getIsAltTextUnique: () => true
    };

    constructor(props) {
        super(props);

        this.state = {
            dropzoneActive: false,
            isUploading: false,
            isSaving: false,
            isSavingCrop: false,
            isCropping: false,
            isReplacing: false,
            replacingImageId: null,
            isWarningAboutPromoting: false,
            error: '',
            uploadPercentCompleted: 0,
            uploadStatus: '',
            originalImage: this.props.originalImage,
            croppedImage: this.props.croppedImage,
            altText: this.props.altText,
            caption: this.props.caption,
            credit: this.props.credit,
            isAltTextInvalid: false,
            shouldFocusWrapper: false,
            isFormValid: true,
            isDeleteModalOpen: false
        };

        this.lastSavedState = {
            originalImage: this.props.originalImage,
            croppedImage: this.props.croppedImage,
            altText: this.props.altText,
            caption: this.props.caption,
            credit: this.props.credit,
        };

        this.imageHasBeenPublished = true;
        this.fieldRefs = [];
    }


    componentDidMount() {
        if (this.props.showFilesInput) {
            this.openFilesInput()
        }
        if (this.props.showCropOnMount) {
            this.setState({
                isCropping: true,
            })
        }
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (nextProps !== this.props) {
            if (
                (this.state.altText && !nextProps.altText) ||
                (this.state.caption && !nextProps.caption) ||
                isEqual(this.state.originalImage, nextProps.originalImage)) {
                return;
            }

            this.setState({
                originalImage: nextProps.originalImage,
                croppedImage: nextProps.croppedImage,
                altText: nextProps.altText,
                caption: nextProps.caption,
                credit: nextProps.credit,
                isReplacing: false,
                replacingImageId: null
            });

            this.lastSavedState = {
                originalImage: nextProps.originalImage,
                croppedImage: nextProps.croppedImage,
                altText: nextProps.altText,
                caption: nextProps.caption,
                credit: nextProps.credit
            };

            // If this is a preview image, the useHeroImage function will send a
            // new original image and an empty cropped image ... we need to trigger
            // the crop tool.
            if (this.props.imageType === 'preview' && nextProps.originalImage.source && !nextProps.croppedImage.source) {
                this.setState({
                    isCropping: true
                });
            }
        }
    }


    onDragEnter = () => {
        this.setState({
            dropzoneActive: true
        });
    };

    onDragLeave = () => {
        this.setState({
            dropzoneActive: false
        });
    };

    onDropRejected = () => {
        this.setState({
            error: 'Make sure your image is a .jpg, .jpeg, or .png file and try again.'
        });
    };

    onDrop = (acceptedFiles) => {
        this.setState({
            dropzoneActive: false,
            shouldFocusWrapper: true
        });

        // acceptedFiles is always an array, even if only one file was uploaded.
        // Multiples are not allowed, so we grab the first item.
        const file = acceptedFiles[0];

        if (file) {
            // Prevent memory leaks
            window.URL.revokeObjectURL(file.preview);

            const reader = new FileReader();

            reader.onload = (e) => {
                const result = e.target.result;
                const img = new Image();

                img.onload = async () => {
                    const exifOrientation = await getOrientation(result);

                    const droppedImage = {
                        source: result,
                        name: generateImageName(file.name),
                        size: file.size,
                        height: img.height,
                        width: img.width,
                        exifOrientation
                    };

                    // If the dropped image has a long edge longer than 4000px,
                    // we're going to downsize it to avoid crazy big images
                    // from getting uploaded
                    //const originalImage = await optimizeImage(droppedImage);
                    // ^^-- this is buggy - exif rotations are not right at least.. plus not needed- backend takes care of image optimizations already
                    const originalImage = droppedImage;
                    let croppedImage = {};

                    // Gallery images don't automatically trigger the cropping tool,
                    // so cropped is equal to original
                    croppedImage = {
                        ...originalImage,
                        pixelCrop: {
                            x: 0,
                            y: 0,
                            width: originalImage.width,
                            height: originalImage.height
                        }
                    };

                    // Image must be loaded to get width and height.
                    // Put new photo in state and clear previous croppedImage
                    this.setState({
                        originalImage,
                        croppedImage,
                        isCropping: this.props.showCropOnSelect,
                    },()=>{
                        if (!this.state.isCropping) {
                            this.props.onUpdate({
                                ...this.state
                            });
                        }
                    });
                };

                img.src = result;
            };

            reader.readAsDataURL(file);
        }else{
            this.setState({
                isCropping: false,
            });
        }
    };

    toggleDeleteModal = () => {
        this.setState({ isDeleteModalOpen: !this.state.isDeleteModalOpen });
    };

    addFieldRef = (index) => (ref) => {
        this.fieldRefs[index] =  ref;
    };

    handleInputChange = (event) => {
        this.setState({
            [event.target.name]: event.target.value
        });
    }

    saveCroppedPhoto = (pixelCrop) => {
        const { originalImage } = this.state;

        if (!this.imageHasBeenPublished) {
            cropImageToDataURL(originalImage, pixelCrop).then((res) => {
                const img = new Image();

                img.onload = () => {
                    this.setState({
                        isCropping: false,
                        croppedImage: {
                            source: res.dataURL,
                            name: generateImageName(originalImage.name, 'jpg', 'cropped'),
                            // approx file size because the Base64 encoding has a 4/3 overhead
                            size: Math.round((res.dataURL.length * 3) / 4),
                            height: img.height,
                            width: img.width,
                            percentCrop: res.cropData,
                            pixelCrop
                        }
                    }, ()=> {
                        this.props.onUpdate({
                            ...this.state
                        });
                    });
                };

                img.src = res.dataURL;
            });
        }
        else {
            this.setState({
                isCropping: false,
                croppedImage: {
                    ...this.state.croppedImage,
                    pixelCrop
                }
            }, () => {
                this.uploadNewImageCrop();
            });
        }

        if (this.state.shouldFocusWrapper) {
            if (this.wrapperRef && this.wrapperRef.focus) {
                this.wrapperRef.focus()
            }
        }
    };

    editPhotoCrop = (e) => {
        e.preventDefault();

        const { croppedImage, originalImage } = this.state;
        const pixelCrop = croppedImage.pixelCrop;

        if (croppedImage.percentCrop === undefined) {
            const percentCrop = {
                x: pixelCrop.x / originalImage.width * 100,
                y: pixelCrop.y / originalImage.height * 100,
                width: pixelCrop.width / originalImage.width * 100,
                height: pixelCrop.height / originalImage.height * 100
            };

            this.setState({
                isCropping: true,
                croppedImage: {
                    ...this.state.croppedImage,
                    percentCrop
                }
            });
        }
        else {
            this.setState({
                isCropping: true
            });
        }
    };

    replacePhoto = (e) => {
        e.preventDefault();
        this.setState({
            isReplacing: true,
            replacingImageId: this.props.originalImage.mediaId
        }, ()=>{
            this.openFilesInput()
        });
    };

    movePositionUp = (e) => {
        e.preventDefault();

        const { position, reorderGallery } = this.props;

        if (position > 0) {
            reorderGallery({
                oldIndex: position,
                newIndex: position - 1
            });
        }
    };

    movePositionDown = (e) => {
        e.preventDefault();

        const { position, reorderGallery, numberOfGalleryItems } = this.props;

        if (position < numberOfGalleryItems - 1) {
            reorderGallery({
                oldIndex: position,
                newIndex: position + 1
            });
        }
    };

    deletePhotoAndText = (e) => {
        e.preventDefault();
        const { uploadManager, id } = this.props;
        const mediaId = this.state.croppedImage.mediaId;

        this.setState({
            isDeleteModalOpen: false
        });

        if (mediaId && mediaId.length) {
            const newState = {
                isSaving: false,
                originalImage: {},
                croppedImage: {},
                altText: '',
                caption: '',
                credit: ''
            };

            const onError = (errorObject) => {
                const error = errorObject?.response?.data?.error;
                const fallback = 'The server had trouble deleting the photo. Please try again.';

                this.setState({
                    error: error || fallback,
                    isSaving: false
                });
            };

            const onCompletion = (res) => {
                if (res && !res.data.err) {
                    this.lastSavedState = newState;

                    this.setState({
                        ...this.state,
                        ...newState
                    });

                    // Invoke callback
                    this.props.onDelete(id);
                }
                else {
                    onError(res.data.err);
                }
            };

            const task = {
                type: DELETE,
                image: this.generatePhotoProperties(),
                onCompletion,
                onError
            };

            this.setState({
                isSaving: true
            });

            // Remove data from the server
            uploadManager.addTask(task);
        }
    };

    publishChanges = (e) => {
        e.preventDefault();

        const { originalImage, croppedImage, altText, caption, credit } = this.state;

        // Abort if all information is not present
        if (!originalImage.source || !croppedImage.source || !altText || !caption || !credit) {
            return;
        }

        if (croppedImage.source.indexOf('https:') === -1) {
            // New image, so upload everything
            this.uploadPhoto();
        }
        else {
            // Image hasn't changed so just upload changes
            this.uploadChanges();
        }
    };

    uploadNewImageCrop = () => {
        const { uploadManager, onPublish } = this.props;
        const { croppedImage } = this.state;
        const mediaId = this.state.croppedImage.mediaId;

        if (mediaId && mediaId.length) {
            const onError = (errorObject) => {
                const error = errorObject?.response?.data?.error;
                const fallback = 'The server had trouble saving the cropped photo. Please try again.';

                this.setState({
                    error: error || fallback,
                    isSaving: false
                });
            };

            const onCompletion = (res) => {
                if (res && !res.data.err) {
                    const media = res.data.media;
                    const savedOriginalImage = {
                        mediaId: media.media_id,
                        source: media.url,
                        width: parseInt(media.width, 10),
                        height: parseInt(media.height, 10),
                        name: media.media_id,
                        type: media.mime_type
                    };
                    const savedCroppedImage = {
                        mediaId: media.media_id,
                        source: media.url,
                        width: parseInt(media.width, 10),
                        height: parseInt(media.height, 10),
                        name: media.media_id,
                        type: media.mime_type,
                        pixelCrop: {
                            x: media.crop_x,
                            y: media.crop_y,
                            width: media.crop_width,
                            height: media.crop_height
                        }
                    };

                    // Timeout to prevent 403 errors
                    setTimeout(() => {

                        this.lastSavedState.croppedImage = croppedImage;

                        this.setState({
                            originalImage: savedOriginalImage,
                            croppedImage: savedCroppedImage,
                            isCropping: false,
                            isReplacing: false,
                            replacingImageId: null,
                            isSavingCrop: false,
                        }, () => {
                            // Invoke callback
                            onPublish();
                        });
                    }, 1000);
                }
                else {
                    onError(res.data.err);
                }
            };

            const photoProperties = {
                media_id: croppedImage.mediaId,
                crop_x: croppedImage.pixelCrop.x,
                crop_y: croppedImage.pixelCrop.y,
                crop_width: croppedImage.pixelCrop.width,
                crop_height: croppedImage.pixelCrop.height
            };

            const task = {
                type: CROP,
                image: photoProperties,
                onCompletion,
                onError
            };

            this.setState({
                isSavingCrop: true
            });

            // Save data to the server
            uploadManager.addTask(task);
        }
    };

    uploadChanges = () => {
        const { uploadManager, onPublish } = this.props;
        const mediaId = this.state.croppedImage.mediaId;

        if (mediaId && mediaId.length) {
            const onError = (errorObject) => {
                const error = errorObject?.response?.data?.error;
                const fallback = 'The server had trouble saving your changes. Please try again.';

                this.setState({
                    error: error || fallback,
                    isSaving: false
                });
            };

            const onCompletion = (res) => {
                if (res && !res.data.err) {
                    this.lastSavedState = {
                        originalImage: this.state.originalImage,
                        croppedImage: this.state.croppedImage,
                        altText: this.state.altText,
                        caption: this.state.caption,
                        credit: this.state.credit
                    };

                    this.setState({
                        isSaving: false,
                        isCropping: false,
                        isReplacing: false,
                        replacingImageId: null
                    }, ()=> {
                        // Invoke callback
                        onPublish();
                    });
                }
                else {
                    onError(res.data.err);
                }
            };

            const task = {
                type: UPDATE,
                image: this.generatePhotoProperties(),
                onCompletion,
                onError
            };

            this.setState({
                isSaving: true
            });

            // Save data to the server
            uploadManager.addTask(task);
        }
    };

    uploadPhoto = () => {
        const { imageType, uploadManager, galleryPosition, maxGalleryPosition, numberOfGalleryItems } = this.props;
        const { croppedImage, originalImage, altText, caption, credit } = this.state;

        // Bail if file is too large.  This is a last-ditch effort to avoid a
        // 413 error that can't be caught via JavaScript
        if (croppedImage.size > 16000000) {
            this.setState({
                isUploading: false,
                error: 'Your file is too large. Images should be 16MB or less.',
                uploadPercentCompleted: 0,
                uploadStatus: ''
            });

            return;
        }

        // Define onProgress function
        const onProgress = (progressEvent) => {
            const percentCompleted = progressEvent.loaded / progressEvent.total;
            const uploadStatus = `${((progressEvent.total / 1024) * percentCompleted).toFixed(2)} kb of ${(progressEvent.total / 1024).toFixed(2)} kb`;
            const uploadPercentCompleted = Math.round(percentCompleted * 100);



            if (uploadPercentCompleted < 0.85) {
                this.setState({
                    uploadStatus,
                    uploadPercentCompleted: uploadPercentCompleted - 15
                });
            }
            else {
                this.setState({
                    uploadStatus: 'Processing file ...',
                    uploadPercentCompleted: 87
                });
            }
        };

        // Define onCompletion
        const onCompletion = (res) => {
            if (res && !res.data.err) {
                const media = res.data.media;

                this.setState({
                    uploadStatus: 'Finalizing upload ...',
                    uploadPercentCompleted: 92
                });

                // Yay nested setTimeouts!
                // Chaining setStates to manually move the progress bar so it
                // doesn't seem like the upload stalled. The 2+ second delay is
                // so the server-side processes can finish and we can avoid
                // 403 errors when the component updates
                setTimeout(() => {
                    this.setState({
                        uploadStatus: 'Finalizing upload ...',
                        uploadPercentCompleted: 97
                    });

                    setTimeout(() => {
                        this.setState({
                            uploadStatus: 'Success!',
                            uploadPercentCompleted: 100
                        });

                        setTimeout(() => {
                            // Below is some trickery to prevent slow image loads
                            // and 403 errors. We're not going to update the component
                            // with the real URL of the image, and instead going to keep
                            // using the dataURL.
                            //
                            // To do this, we have to add the mediaId to the state
                            // and the lastSavedState
                            this.lastSavedState = {
                                id: this.props.id,
                                altText: media.title,
                                caption: media.description,
                                credit: media.credits,
                                originalImage: {
                                    mediaId: media.media_id,
                                    ...this.state.originalImage
                                },
                                croppedImage: {
                                    mediaId: media.media_id,
                                    ...this.state.croppedImage
                                }
                            };

                            this.setState(
                                {
                                    isUploading: false,
                                    uploadPercentCompleted: 0,
                                    uploadStatus: '',
                                    originalImage: {
                                        mediaId: media.media_id,
                                        ...this.state.originalImage
                                    },
                                    croppedImage: {
                                        mediaId: media.media_id,
                                        ...this.state.croppedImage
                                    }
                                },
                                () => {

                                    if (!this.state.isReplacing) {
                                        const newMediaItem = {
                                            id: this.props.id,
                                            altText: media.title,
                                            caption: media.description,
                                            credit: media.credits,
                                            is_primary: this.props.imageType === 'hero',
                                            is_preview: this.props.imageType === 'preview',
                                            is_gallery: this.props.imageType === 'gallery',
                                            originalImage: {
                                                mediaId: media.media_id,
                                                source: media.url,
                                                width: parseInt(media.width, 10),
                                                height: parseInt(media.height, 10),
                                                name: media.media_id,
                                                type: media.mime_type
                                            },
                                            croppedImage: {
                                                mediaId: media.media_id,
                                                source: media.url,
                                                width: parseInt(media.width, 10),
                                                height: parseInt(media.height, 10),
                                                name: media.media_id,
                                                type: media.mime_type,
                                                pixelCrop: {
                                                    x: media.crop_x,
                                                    y: media.crop_y,
                                                    width: media.crop_width,
                                                    height: media.crop_height
                                                }
                                            }
                                        };

                                        this.props.onPublish(newMediaItem, this.state.originalImage);
                                    }

                                    // Delete original image if this is a replacement
                                    if (this.state.isReplacing && this.state.replacingImageId) {
                                        const deleteTask = {
                                            type: DELETE,
                                            image: { media_id: this.state.replacingImageId },
                                            onCompletion: () => {
                                                this.setState({
                                                    isCropping: false,
                                                    isReplacing: false,
                                                    replacingImageId: null,
                                                }, () => {
                                                    this.props.onPublish();
                                                });
                                            },
                                            // eslint-disable-next-line no-console
                                            onError: (err) => console.warn('replace > delete image error', err)
                                        };

                                        uploadManager.addTask(deleteTask);
                                    }else{
                                        this.props.onPublish();
                                    }
                                }
                            );
                        }, 2500);
                    }, 700);
                }, 700);
            }
            else {
                // eslint-disable-next-line no-console
                console.log('uploadPhoto - API completion but res.data.err is truthy', res.data.err);
                this.setState({
                    error: 'The server had trouble saving your photo. Please try again.',
                    uploadPercentCompleted: 0,
                    uploadStatus: ''
                });
            }
        };

        // Define onError
        const onError = (errorObject) => {
            const error = errorObject?.response?.data?.error;
            const fallback = 'The server had trouble saving your photo. Please try again.';

            this.setState({
                isUploading: false,
                error: error || fallback,
                uploadPercentCompleted: 0,
                uploadStatus: ''
            });
        };

        // Create task object
        let position = galleryPosition;
        if (galleryPosition === 0 && numberOfGalleryItems > 0) {
            position = maxGalleryPosition + 1;
        }

        const task = {
            type: UPLOAD,
            image: {
                ...originalImage,
                pixelCrop: croppedImage.pixelCrop,
                altText,
                caption,
                credit,
                type: imageType,
                position
            },
            onProgress,
            onCompletion,
            onError
        };



        this.setState({ isUploading: true });

        // Start upload
        uploadManager.addTask(task);
    };

    useHeroImage = (e) => {
        e.preventDefault();

        const newState = this.props.getHeroImage();

        this.setState({
            ...this.state,
            ...newState,
            isCropping: true
        });
    };

    openFilesInput = (e) => {
        if (e) {
            e.preventDefault()
        }
        const input = document.getElementById(`photo-files-input-${this.props.id}`);
        input.value = '';
        input.click();
    };

    cancelCrop = () => {
        // For hero and preview images, canceling means dumping the dragged image entirely
        if (this.lastSavedState.originalImage.source && !this.lastSavedState.croppedImage.source) {
            this.lastSavedState.originalImage = {};
            this.lastSavedState.croppedImage = {};
        }

        this.setState({
            isCropping: false,
            originalImage: this.lastSavedState.originalImage,
            croppedImage: this.lastSavedState.croppedImage
        });
    };

    cancelEditing = (e) => {
        e.preventDefault();

        const { onCancel, id } = this.props;

        this.setState({
            ...this.state,
            ...this.lastSavedState
        });

        // Invoke callback
        onCancel({
            id,
            ...this.state
        });
    };

    clearError = () => {
        this.setState({
            error: ''
        });
    };

    generatePhotoProperties() {
        const { croppedImage, altText, caption, credit } = this.state;
        return {
            media_id: croppedImage.mediaId,
            url: croppedImage.source,
            title: altText,
            description: caption,
            credits: credit,
            is_primary: this.props.imageType === 'hero',
            is_preview: this.props.imageType === 'preview',
            is_gallery: this.props.imageType === 'gallery',
            crop_x: croppedImage.pixelCrop.x,
            crop_y: croppedImage.pixelCrop.y,
            crop_width: croppedImage.pixelCrop.width,
            crop_height: croppedImage.pixelCrop.height,
            position: this.props.galleryPosition
        };
    }

    toggleWarningAboutPromoting = () => {
        this.setState({
            isWarningAboutPromoting: !this.state.isWarningAboutPromoting
        });
    }

    handleNewErrorState = ({ isInvalid }) => {
        this.setState({
            isAltTextInvalid: isInvalid
        });
    }

    maxLengthValidation = (input) => {
        const MAX_VALUE = 2000;
        const parsed = {
            isValid: true,
            errorText: null
        };

        if(!isEmpty(input) && input.length > MAX_VALUE) {
            parsed.isValid = false;
            parsed.errorText = `The maximum character length is ${MAX_VALUE}.`;
        }
        return parsed;
    }

    altTextValidator = (input) => {
        const { getIsAltTextUnique } = this.props;
        const forbiddenWords = ['PHOTO', 'PICTURE', 'IMAGE'];
        const inputUpperCase = input.toUpperCase();

        const parsed = {
            isValid: true,
            errorText: null
        };

        const isAltTextUnique = getIsAltTextUnique(input, this.props.id);

        const maxValidation = this.maxLengthValidation();
        if(!maxValidation.isValid) {
            return maxValidation;
        }

        if (!isAltTextUnique) {
            parsed.isValid = false;
            parsed.errorText = 'There is already another image that uses this alt text, and this info must be unique for every image'
        }

        forbiddenWords.forEach((word) => {
            if (inputUpperCase.startsWith(word)) {
                parsed.isValid = false;
                parsed.errorText = 'Screen readers inform users that they are hearing the description of an image, so alt text should not include the words "photo", "picture", or "image"'
            }
        });

        return parsed;
    }

    validateForm = () => {
        const errors = [];
        // putting a timeout here because we want to have the latest value on the child component
        // before performing a validation
        setTimeout( () => {
            this.fieldRefs.forEach((ref) => {
                const validation = ref.validate();

                if (validation) {
                    if (validation.isInvalid) {
                        errors.push({
                            inputId: validation.id,
                            errorText: validation.errorText
                        });
                    }
                }
            });
            const isFormValid = errors.length === 0 && this.state.croppedImage?.source;
            this.setState({ isFormValid });
        }, 0);
    }

    renderPhotoPreview() {
        const { croppedImage, altText, isSavingCrop } = this.state;
        const { desiredWidth, desiredHeight } = this.props;
        const isImageTooSmall = croppedImage.width < desiredWidth || croppedImage.height < desiredHeight;

        return (
            <div className="rec-photo-manager-thumbnail">
                <img
                    className="rec-photo-manager-thumbnail-image"
                    src={croppedImage.source}
                    alt={altText}
                    onLoad={() => this.setState({ isSavingCrop: false })} />
                {isImageTooSmall && (
                    <div className="rec-photo-manager-size-reporter">
                        <div className={cx({ 'rec-photo-manager-error-text': isImageTooSmall })}>
                            Current Dimensions:{' '}
                            <span className="nowrap">
                                <strong>{croppedImage.width}</strong> x{' '}
                                <strong>
                                    {croppedImage.height}
                                    px
                                </strong>
                            </span>
                        </div>
                        <div>
                            Minimum Dimensions:{' '}
                            <span className="nowrap">
                                {desiredWidth} x {desiredHeight}
                                px
                            </span>
                        </div>
                    </div>
                )}

                <FlexRow className="rec-photo-manager-thumbnail-actions">
                    <FlexCol xs={12} className="mb-1">
                        <Button
                            shouldFitContainer
                            appearance="tertiary"
                            onClick={this.replacePhoto} >
                                Replace Image
                        </Button>
                    </FlexCol>

                    <FlexCol xs={12} className="mb-1">
                        <Button
                            shouldFitContainer
                            appearance="tertiary"
                            onClick={this.editPhotoCrop}>
                                Edit Image Crop
                        </Button>
                    </FlexCol>
                </FlexRow>

                {isSavingCrop && (
                    <div className="rec-photo-manager-thumbnail-loading">
                        <Loading />
                    </div>
                )}
            </div>
        );
    }

    renderPhotoUpload() {
        const { desiredWidth, desiredHeight } = this.props;

        return (
            <React.Fragment>
                <div className="rec-photo-manager-upload-controls">
                    <Button onClick={this.openFilesInput}>
                        Upload an Image
                    </Button>

                    {/* this only works for newly selected (that are not urls) non published photos and really pretty much never
                    id === 'preview' && hasHeroImage && (
                        <Button onClick={this.useHeroImage} isDisabled={!hasHeroImage}>
                            Use Hero Image
                        </Button>
                    ) */}
                </div>
                <FlexRow>
                    <FlexCol xs={12}>
                        Preferred Image Dimensions:{' '}
                        <span className="nowrap">
                            <strong>{desiredWidth}</strong> x <strong>{desiredHeight}</strong>
                            px
                        </span>
                    </FlexCol>

                    <FlexCol xs={12} className="mb-2">
                        Accepted file types: <strong>jpg</strong>, <strong>jpeg</strong>, <strong>png</strong>
                    </FlexCol>
                </FlexRow>
            </React.Fragment>
        );
    }

    renderHeroPhotoUpload() {
        const { heroImageName, desiredWidth, desiredHeight} = this.props;

        return (
            <React.Fragment>
                <Heading headingLevel={4} appearance="h5" className="mb-2">
                    {heroImageName}
                </Heading>
                <FlexRow>
                    <FlexCol xs={12}>
                        Preferred Image Dimensions:{' '}
                        <span className="nowrap">
                            <strong>{desiredWidth}</strong> x <strong>{desiredHeight}</strong>
                            px
                        </span>
                    </FlexCol>

                    <FlexCol xs={12} className="mb-2">
                        Accepted file types: <strong>jpg</strong>, <strong>jpeg</strong>, <strong>png</strong>
                    </FlexCol>

                    <FlexCol xs={12} className="mb-2">
                        <Button
                            shouldFitContainer
                            appearance="tertiary"
                            onClick={this.openFilesInput}>
                            Upload an Image
                        </Button>
                    </FlexCol>
                </FlexRow>
            </React.Fragment>
        );
    }

    renderHeroPhotoPreview() {
        const { croppedImage, isSavingCrop } = this.state;
        const { desiredWidth, desiredHeight, heroImageName } = this.props;
        const isImageTooSmall = croppedImage.width < desiredWidth || croppedImage.height < desiredHeight;

        return (
            <div className="rec-photo-manager-thumbnail">
                <Heading headingLevel={4} appearance="h5" className="mb-2">
                    {heroImageName}
                </Heading>

                {isImageTooSmall && (
                    <div className="rec-photo-manager-size-reporter">
                        <div className={cx({ 'rec-photo-manager-error-text': isImageTooSmall })}>
                            Current Dimensions:{' '}
                            <span className="nowrap">
                                <strong>{croppedImage.width}</strong> x{' '}
                                <strong>
                                    {croppedImage.height}
                                    px
                                </strong>
                            </span>
                        </div>
                        <div>
                            Minimum Dimensions:{' '}
                            <span className="nowrap">
                                {desiredWidth} x {desiredHeight}
                                px
                            </span>
                        </div>
                    </div>
                )}

                <FlexRow>
                    <FlexCol xs={12}>
                        Preferred Image Dimensions:{' '}
                        <span className="nowrap">
                            <strong>{desiredWidth}</strong> x <strong>{desiredHeight}</strong>
                            px
                        </span>
                    </FlexCol>

                    <FlexCol xs={12} className="mb-2">
                        Accepted file types: <strong>jpg</strong>, <strong>jpeg</strong>, <strong>png</strong>
                    </FlexCol>

                    <FlexCol xs={12} className="mb-1">
                        <Button
                            shouldFitContainer
                            appearance="tertiary"
                            onClick={this.replacePhoto}>
                                Replace Image
                        </Button>
                    </FlexCol>

                    <FlexCol xs={12} className="mb-3 mb-md-0">
                        <Button
                            shouldFitContainer
                            appearance="tertiary"
                            onClick={this.editPhotoCrop}
                            className="rec-photo-manager-text-button">
                                Edit Image Crop
                        </Button>
                    </FlexCol>
                </FlexRow>

                {isSavingCrop && (
                    <div className="rec-photo-manager-thumbnail-loading">
                        <Loading />
                    </div>
                )}
            </div>
        );
    }

    deleteModal = () => {
        return (
            <StyledModal
                shouldShowCloseButton={false}
                size="md"
                isOpen={this.state.isOpen}
                heading="Transaction History"
                onRequestClose={this.closeModal} >

                <ModalActions>
                    <ButtonGroup
                        isFullWidthOnMobile={false}
                        isStretchedToFit>
                        <Button
                            appearance="tertiary"
                            onClick={this.closeModal}>
                            Cancel
                        </Button>
                        <Button onClick={this.closeModal}>
                            Ok
                        </Button>
                    </ButtonGroup>
                </ModalActions>
            </StyledModal>
        )
    }

    render() {
        const {
            imageType,
            imageTypeDisplay,
            id,
            desiredWidth,
            desiredHeight,
            position,
            numberOfGalleryItems
        } = this.props;
        const {
            dropzoneActive,
            originalImage,
            croppedImage,
            altText,
            caption,
            credit,
            isUploading,
            isSaving,
            error,
            isCropping,
            isWarningAboutPromoting,
            isAltTextInvalid,
            isFormValid,
            isDeleteModalOpen
        } = this.state;
        const hasError = error && error.length > 0;
        const isFormEmpty = !croppedImage.source && !altText && !caption && !credit;
        const hasFormChanged =
            this.lastSavedState.altText !== altText ||
            this.lastSavedState.caption !== caption ||
            this.lastSavedState.credit !== credit ||
            (this.state.isReplacing && this.state.replacingImageId) ||
            (croppedImage.mediaId === undefined && !isFormEmpty);

        const isEditingActive = dropzoneActive || hasFormChanged || isUploading || isSaving || hasError;

        if (typeof originalImage === 'object' && originalImage.source) {
            this.imageHasBeenPublished = originalImage.source.indexOf(';base64') === -1;
        }

        if(hasFormChanged) this.validateForm();

        return (
            <div className="rec-single-photo-manager">
                <Dropzone
                    disableClick
                    multiple={false}
                    style={{ position: 'relative' }}
                    accept="image/jpeg, image/png"
                    onDrop={this.onDrop}
                    onDragEnter={this.onDragEnter}
                    onDropRejected={this.onDropRejected}
                    onDragLeave={this.onDragLeave}
                    inputProps={{ id: `photo-files-input-${id}`, className: 'rec-photo-manager-files-input' }}>
                    {hasError && (
                        <FocusOnMount>
                            <div className="rec-photo-manager-overlay rec-photo-manager-error-overlay active">
                                <div className="rec-photo-manager-error-icon">
                                    <Icons.IconError />
                                </div>
                                <h3>
                                    Sorry, there was an error
                                </h3>
                                <p>{error}</p>
                                <Button onClick={this.clearError}>
                                    OK
                                </Button>
                            </div>
                        </FocusOnMount>
                    )}

                    <FlexRow>
                        <FlexCol
                            className={cx('rec-photo-manager', { active: isEditingActive })}
                            xl={10}
                            xlOffset={1}
                            ref={(node) => this.wrapperRef = node}
                            tabIndex="-1">
                            <form className="rec-photo-manager-form" onSubmit={this.publishChanges}>
                                <FlexRow>
                                    <FlexCol md={4} xxl={3} className="rec-photo-manager-left-col">
                                        {this.props.isHeroImage ? (
                                            <React.Fragment>
                                                {typeof croppedImage === 'object' && croppedImage.source
                                                ? this.renderHeroPhotoPreview(isEditingActive)
                                                : this.renderHeroPhotoUpload(isEditingActive)}
                                            </React.Fragment>
                                            ) : (
                                            <React.Fragment>
                                                {typeof croppedImage === 'object' && croppedImage.source
                                                ? this.renderPhotoPreview()
                                                : this.renderPhotoUpload()}
                                            </React.Fragment>
                                            )}
                                    </FlexCol>

                                    <FlexCol md={8} xxl={9} className="rec-photo-manager-right-col">
                                        <div className="rec-photo-manager-form-item-block">
                                            <div className="rec-form-item-wrap">
                                                <TextField
                                                    key={this.state.originalImage.source}
                                                    id={`rec-photo-manager-alt-text-${id}`}
                                                    ref={this.addFieldRef(0)}
                                                    name="altText"
                                                    label="Alternative Text:"
                                                    isRequired
                                                    placeholder="Very short and unique description used for web accessibility and screen readers."
                                                    value={altText}
                                                    onChange={this.handleInputChange}
                                                    validator={this.altTextValidator}
                                                    handleNewErrorState={this.handleNewErrorState}
                                                    isInvalid={this.state.originalImage.source && (!altText || altText === '')}
                                                    shouldValidateOnMount
                                                    shouldValidateOnUpdate />
                                            </div>

                                            <div className="rec-form-item-wrap">
                                                <TextArea
                                                    id={`rec-photo-manager-caption-${id}`}
                                                    isRequired
                                                    value={caption}
                                                    ref={this.addFieldRef(1)}
                                                    onChange={this.handleInputChange}
                                                    label="Caption Text"
                                                    rows="2"
                                                    name="caption"
                                                    validator={this.maxLengthValidation}
                                                    isInvalid={this.state.originalImage.source && (!caption || caption === '')}
                                                    shouldValidateOnUpdate
                                                    placeholder="Description displayed with the photo" />
                                            </div>

                                            <div className="rec-form-item-wrap">
                                                <TextField
                                                    id={`rec-photo-manager-credit-${id}`}
                                                    name="credit"
                                                    label="Photo Credit:"
                                                    isRequired
                                                    ref={this.addFieldRef(2)}
                                                    placeholder="Person/organization who owns the photo. Displayed with the photo."
                                                    validator={this.maxLengthValidation}
                                                    value={credit}
                                                    onChange={this.handleInputChange}
                                                    isInvalid={this.state.originalImage.source && (!credit || credit === '')}
                                                    shouldValidateOnMount
                                                    shouldValidateOnUpdate />
                                            </div>
                                        </div>

                                        <div className="rec-photo-manager-action-buttons">
                                            <div className="rec-photo-manager-action-buttons-left">
                                                {!this.props.isHeroImage && this.props.imageType !== "preview" && (
                                                    <div className="rec-photo-gallery-item-position-btn-wrapper">
                                                        <button
                                                            className="rec-button-tertiary rec-photo-gallery-item-position-btn"
                                                            onClick={this.movePositionUp}
                                                            disabled={position === 0 || isEditingActive}
                                                            aria-label="Move position up">
                                                            <Icons.IconChevronUp alt="" />
                                                        </button>
                                                        <button
                                                            className="rec-button-tertiary rec-photo-gallery-item-position-btn"
                                                            onClick={this.movePositionDown}
                                                            disabled={position === numberOfGalleryItems - 1 || isEditingActive}
                                                            aria-label="Move position down">
                                                            <Icons.IconChevronDown alt="" />
                                                        </button>
                                                        {position !== -1 && (
                                                            <div className="rec-photo-gallery-position-label">
                                                                Image {position + 1} of {numberOfGalleryItems}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="rec-photo-manager-action-buttons-right">

                                                {!hasFormChanged && !isFormEmpty && (
                                                    <ButtonGroup>
                                                        {originalImage?.source.indexOf('https:') >= 0 &&
                                                            <Button
                                                                iconBeforeElement={<Icons.IconFileDownload />}
                                                                appearance="link"
                                                                href={originalImage.source}
                                                                alt="download original image"
                                                                rel="noreferrer"
                                                                target="_blank"
                                                                download>
                                                                    Download Original Image
                                                            </Button>
                                                        }

                                                        <Button
                                                            iconBeforeElement={<Icons.IconDelete />}
                                                            appearance="link"
                                                            alt="delete photo and text"
                                                            onClick={this.toggleDeleteModal}
                                                            disabled={isFormEmpty}>
                                                                Delete Photo and Text
                                                        </Button>
                                                    </ButtonGroup>
                                                )}

                                                {hasFormChanged && (
                                                    <button type="button" className="rec-photo-manager-text-button" onClick={this.cancelEditing} disabled={isFormEmpty}>
                                                        Cancel Editing
                                                    </button>
                                                )}

                                                {(hasFormChanged || isUploading) && (
                                                    <button type="submit" className="rec-button-primary" disabled={!hasFormChanged || !isFormValid || isAltTextInvalid}>
                                                        {!isFormValid ? 'Complete All Fields Before Publishing' : `Publish ${imageTypeDisplay}`}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </FlexCol>
                                </FlexRow>
                            </form>

                            <div className={cx('rec-photo-manager-overlay', 'rec-photo-dropzone-overlay', { active: dropzoneActive })}>
                                <Icons.IconCloudUpload />
                                <h3>Drop File To Upload a New {imageTypeDisplay}</h3>
                            </div>

                            <div className={cx('rec-photo-manager-overlay', { active: isUploading })}>
                                <UploadProgressIndicator
                                    name={`Publishing ${imageTypeDisplay}`}
                                    status={this.state.uploadStatus}
                                    progress={this.state.uploadPercentCompleted} />
                            </div>

                            <div className={cx('rec-photo-manager-overlay', { active: isSaving })}>
                                <Loading />
                            </div>
                        </FlexCol>
                    </FlexRow>
                </Dropzone>

                <StyledModal
                    heading={`${imageTypeDisplay} Crop`}
                    size="lg"
                    isOpen={isCropping}
                    onRequestClose={this.cancelCrop}>
                    <PhotoCropper
                        aspect={imageType === 'hero' ? 16 / 9 : 4 / 3}
                        width={originalImage.width}
                        height={originalImage.height}
                        desiredWidth={desiredWidth}
                        desiredHeight={desiredHeight}
                        uploadImageSource={originalImage.source}
                        saveCroppedPhoto={this.saveCroppedPhoto}
                        cancelCrop={this.cancelCrop}
                        disabled={this.state.saveButtonDisabled}
                        previousCrop={this.state.croppedImage.percentCrop}
                        isFreeform={this.props.isFreeformCrop}
                        imageHasBeenPublished={this.imageHasBeenPublished} />
                </StyledModal>

                {isWarningAboutPromoting && (
                    <StyledModal
                        heading="This May Result in a Low Quality Hero Image"
                        size="lg"
                        isOpen={isWarningAboutPromoting}
                        onRequestClose={this.toggleWarningAboutPromoting}>
                        <PromoteGalleryToHero
                            mediaId={croppedImage.mediaId}
                            uploadManager={this.props.uploadManager}
                            toggleWarningAboutPromoting={this.toggleWarningAboutPromoting}
                            onHeroPromotion={this.props.onHeroPromotion} />
                    </StyledModal>
                )}

                <StyledModal
                    shouldShowCloseButton={false}
                    size="md"
                    isOpen={isDeleteModalOpen}
                    heading="Are you sure you want to remove this image?"
                    onRequestClose={this.toggleDeleteModal}>

                    <ModalActions>
                        <ButtonGroup
                            isFullWidthOnMobile={false}
                            isStretchedToFit>
                            <Button
                                appearance="tertiary"
                                onClick={this.toggleDeleteModal}>
                                Cancel
                            </Button>
                            <Button onClick={this.deletePhotoAndText}>
                                Yes, delete the image and text
                            </Button>
                        </ButtonGroup>
                    </ModalActions>
                </StyledModal>
            </div>
        );
    }
}
